'use client';

import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel from 'embla-carousel-react';
import { useAnimate } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import type { ImageProps } from '@/app/components/media/image/types';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { Butterfly } from '@/app/cv/components/sections/home/hero-banner/item/butterfly';
import { FeatureItem } from '@/app/cv/components/sections/home/lineup/feature-item';
import { type GroupPagination, LineupCarouselOverlay } from '@/app/cv/components/sections/home/lineup/lineup-carousel-overlay';
import { LineupCube, type LineupCubeProps } from '@/app/cv/components/sections/home/lineup/lineup-cube';
import { CATEGORY_LABELS } from '@/app/cv/contents/top-navigation';
import { type CvModelData } from '@/app/cv/types/model-data';
import type { CvEngineModelItemType } from '@/app/cv/types/top-navigation';
import { useInView } from '@/app/hooks/useInView';
import { cn } from '@/app/utils/cn';
import { createDataAttributes, type ExtendDataKeys } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { type CvEngineType } from '@/services/contentstack/models/global-fields/cv-engine-type';
type LineupImage = Omit<ImageProps, 'aspectRatio'>;
export interface SectionLineUpProps {
  engineType: CvEngineType;
  initialSlide: number;
  items: CvEngineModelItemType;
  bg?: LineupImage;
  bgDesktop?: LineupImage;
  cube: LineupCubeProps['cube'];
  logo?: LineupImage;
  logoDecorate?: LineupImage;
}
const resolveGroupPagination = (total: number, groupStarts: number[] = [], groupLabels: string[] = [], selectedIndex: number): GroupPagination | null => {
  if (total === 0) return null;
  for (let i = 0; i < groupStarts.length; i++) {
    const start = groupStarts[i];
    const end = groupStarts[i + 1] ?? total;
    if (selectedIndex >= start && selectedIndex < end) {
      return {
        groupStart: start,
        groupSize: end - start,
        currentPageInGroup: selectedIndex - start + 1,
        groupLabel: groupLabels[i] ?? null
      };
    }
  }
  return {
    groupStart: 0,
    groupSize: total,
    currentPageInGroup: selectedIndex + 1,
    groupLabel: groupLabels[0] ?? null
  };
};
export function SectionLineUp(t0) {
  const $ = _c(107);
  const {
    cube,
    engineType,
    initialSlide,
    bg,
    bgDesktop,
    logo,
    logoDecorate,
    items
  } = t0;
  const [scope, animate] = useAnimate();
  const cubeRef = useRef(null);
  const cubeInnerRef = useRef(null);
  const categoryRef = useRef(null);
  const prevSnapRef = useRef(0);
  const ignoreNextSelectRef = useRef(false);
  const [initialIntroAnimationComplete, setInitialIntroAnimationComplete] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rootMargin: "0px 0px -55% 0px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cubeInview = useInView(cubeRef, t1);
  const rotateXRef = useRef(-28.2044);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "start",
      axis: "x",
      loop: false,
      duration: 25,
      watchDrag: true
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t2);
  let t3;
  if ($[2] !== engineType || $[3] !== items) {
    const g = [];
    const labels = [];
    const models = [];
    const labelMap = CATEGORY_LABELS[engineType];
    Object.entries(items).forEach(t4 => {
      const [categoryKey, category] = t4;
      const before = models.length;
      category?.items?.forEach(item => {
        item.items?.forEach(model => {
          models.push(model);
        });
      });
      if (models.length > before) {
        g.push(before);
        labels.push(labelMap?.[categoryKey] ?? categoryKey);
      }
    });
    t3 = {
      allModel: models,
      groupIndex: g,
      groupLabels: labels
    };
    $[2] = engineType;
    $[3] = items;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    allModel,
    groupIndex,
    groupLabels
  } = t3;
  const {
    selectedIndex
  } = usePaginationSlider(emblaApi);
  let t4;
  if ($[5] !== allModel.length || $[6] !== groupIndex || $[7] !== groupLabels || $[8] !== selectedIndex) {
    t4 = resolveGroupPagination(allModel.length, groupIndex, groupLabels, selectedIndex);
    $[5] = allModel.length;
    $[6] = groupIndex;
    $[7] = groupLabels;
    $[8] = selectedIndex;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const groupPagination = t4;
  let t5;
  if ($[10] !== animate) {
    t5 = () => {
      if (!cubeInnerRef.current) {
        return;
      }
      animate(cubeInnerRef.current, {
        transform: `rotate(60.9099deg) rotateY(-43.3479deg) rotateX(${rotateXRef.current}deg)`
      }, {
        duration: 0.8,
        ease: [0.45, 0, 0.55, 1]
      });
    };
    $[10] = animate;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const animateCubeRotateX = t5;
  let t6;
  if ($[12] !== animateCubeRotateX) {
    t6 = delta => {
      rotateXRef.current = rotateXRef.current + delta;
      animateCubeRotateX();
    };
    $[12] = animateCubeRotateX;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const rotateCubeXBy = t6;
  let t7;
  if ($[14] !== rotateCubeXBy) {
    t7 = () => {
      rotateCubeXBy(180);
    };
    $[14] = rotateCubeXBy;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const rotateCubeXForward = t7;
  let t8;
  if ($[16] !== rotateCubeXBy) {
    t8 = () => {
      rotateCubeXBy(-180);
    };
    $[16] = rotateCubeXBy;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const rotateCubeXBackward = t8;
  let t9;
  if ($[18] !== allModel.length || $[19] !== emblaApi || $[20] !== groupIndex || $[21] !== groupLabels || $[22] !== selectedIndex) {
    t9 = page => {
      if (!emblaApi) {
        return;
      }
      const bounds = resolveGroupPagination(allModel.length, groupIndex, groupLabels, selectedIndex);
      if (!bounds) {
        return;
      }
      emblaApi.scrollTo(bounds.groupStart + page - 1);
    };
    $[18] = allModel.length;
    $[19] = emblaApi;
    $[20] = groupIndex;
    $[21] = groupLabels;
    $[22] = selectedIndex;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const onGroupPageChange = t9;
  let t10;
  bb0: {
    if (groupIndex.length === 0 || !groupPagination) {
      t10 = 0;
      break bb0;
    }
    const i = groupIndex.indexOf(groupPagination.groupStart);
    t10 = i >= 0 ? i : 0;
  }
  const currentGroupIdx = t10;
  const canScrollPrevGroup = currentGroupIdx > 0;
  const canScrollNextGroup = groupIndex.length > 0 && currentGroupIdx < groupIndex.length - 1;
  let t11;
  if ($[24] !== currentGroupIdx || $[25] !== emblaApi || $[26] !== groupIndex) {
    t11 = direction => {
      if (!emblaApi || groupIndex.length === 0) {
        return;
      }
      const nextIdx = currentGroupIdx + direction;
      if (nextIdx < 0 || nextIdx >= groupIndex.length) {
        return;
      }
      emblaApi.scrollTo(groupIndex[nextIdx]);
    };
    $[24] = currentGroupIdx;
    $[25] = emblaApi;
    $[26] = groupIndex;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const scrollToAdjacentGroup = t11;
  let t12;
  let t13;
  if ($[28] !== emblaApi || $[29] !== rotateCubeXBackward || $[30] !== rotateCubeXForward) {
    t12 = () => {
      if (!emblaApi) {
        return;
      }
      prevSnapRef.current = emblaApi.selectedScrollSnap();
      const onSelect = () => {
        const nextSnap = emblaApi.selectedScrollSnap();
        if (ignoreNextSelectRef.current) {
          ignoreNextSelectRef.current = false;
          prevSnapRef.current = nextSnap;
          return;
        }
        if (nextSnap > prevSnapRef.current) {
          rotateCubeXBackward();
        } else {
          if (nextSnap < prevSnapRef.current) {
            rotateCubeXForward();
          }
        }
        prevSnapRef.current = nextSnap;
      };
      emblaApi.on("select", onSelect);
      return () => {
        emblaApi.off("select", onSelect);
      };
    };
    t13 = [emblaApi, rotateCubeXBackward, rotateCubeXForward];
    $[28] = emblaApi;
    $[29] = rotateCubeXBackward;
    $[30] = rotateCubeXForward;
    $[31] = t12;
    $[32] = t13;
  } else {
    t12 = $[31];
    t13 = $[32];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[33] !== allModel.length || $[34] !== emblaApi || $[35] !== initialSlide) {
    t14 = () => {
      if (!emblaApi || allModel.length === 0) {
        return;
      }
      const index = Math.min(Math.max(0, initialSlide), allModel.length - 1);
      ignoreNextSelectRef.current = true;
      prevSnapRef.current = index;
      emblaApi.scrollTo(index, true);
    };
    t15 = [emblaApi, initialSlide, allModel.length];
    $[33] = allModel.length;
    $[34] = emblaApi;
    $[35] = initialSlide;
    $[36] = t14;
    $[37] = t15;
  } else {
    t14 = $[36];
    t15 = $[37];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[38] !== cubeInview) {
    t16 = () => {
      if (!cubeInview) {
        return;
      }
      const timeout = setTimeout(() => {
        setInitialIntroAnimationComplete(true);
      }, 1000);
      return () => clearTimeout(timeout);
    };
    t17 = [cubeInview];
    $[38] = cubeInview;
    $[39] = t16;
    $[40] = t17;
  } else {
    t16 = $[39];
    t17 = $[40];
  }
  useEffect(t16, t17);
  let t18;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = createDataAttributes({
      test: "section-line-up"
    });
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = bg?.src as string;
  const t20 = bg?.alt as string;
  const t21 = bg?.width as number;
  const t22 = bg?.height as number;
  const t23 = bg?.src as string;
  const t24 = bgDesktop?.src as string;
  let t25;
  if ($[42] !== t23 || $[43] !== t24) {
    t25 = generateSources("overall", {
      mobileSrc: t23,
      desktopSrc: t24
    });
    $[42] = t23;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t19 || $[46] !== t20 || $[47] !== t21 || $[48] !== t22 || $[49] !== t25) {
    t26 = <div className="pointer-events-none absolute top-0 left-0 h-1/2 w-full"><Image src={t19} alt={t20} width={t21} height={t22} layout="fill" sources={t25} /></div>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  const t27 = !cubeInview;
  let t28;
  if ($[51] !== t27) {
    t28 = cn("relative", "transition-[opacity,translate] delay-600 duration-600 ease-[cubic-bezier(0.33,1,0.68,1)]", {
      "translate-y-5 opacity-0": t27
    });
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  const t29 = logo?.src as string;
  const t30 = logo?.alt as string;
  const t31 = logo?.width as number;
  const t32 = logo?.height as number;
  let t33;
  if ($[53] !== t29 || $[54] !== t30 || $[55] !== t31 || $[56] !== t32) {
    t33 = <Image src={t29} alt={t30} width={t31} height={t32} aspectRatio="calculate" objectFit="contain" />;
    $[53] = t29;
    $[54] = t30;
    $[55] = t31;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== logoDecorate?.src) {
    t34 = !!logoDecorate?.src && <Butterfly src={logoDecorate?.src as string} variant="lineup" />;
    $[58] = logoDecorate?.src;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  let t35;
  if ($[60] !== t28 || $[61] !== t33 || $[62] !== t34) {
    t35 = <div className="mx-auto w-160/360 max-w-52 lg:ml-6 xl:ml-0"><div className={t28}>{t33}{t34}</div></div>;
    $[60] = t28;
    $[61] = t33;
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  const t36 = cubeInview ? "interactive" : "idle";
  let t37;
  if ($[64] !== cube || $[65] !== t36) {
    t37 = <LineupCube cubeRef={cubeRef} cubeInnerRef={cubeInnerRef} cube={cube} animate={t36} />;
    $[64] = cube;
    $[65] = t36;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = createDataAttributes({
      test: "slides"
    });
    $[67] = t38;
  } else {
    t38 = $[67];
  }
  let t39;
  if ($[68] !== allModel || $[69] !== cubeInview || $[70] !== initialIntroAnimationComplete || $[71] !== selectedIndex) {
    let t40;
    if ($[73] !== cubeInview || $[74] !== initialIntroAnimationComplete || $[75] !== selectedIndex) {
      t40 = (model_0, modelIndex) => {
        const isSlideHidden = selectedIndex !== modelIndex || !cubeInview;
        return <div key={modelIndex} className="min-w-0 shrink-0 basis-full px-6 xl:px-0" {...createDataAttributes({
          test: `slide-${modelIndex}`,
          active: selectedIndex === modelIndex ? "true" : null
        })}><Image src={model_0.image.src as string} alt={model_0.image.alt as string} width={model_0.image.width as number} height={model_0.image.height as number} aspectRatio="aspect-4-3" sizes="(min-width: 624px) 576px, calc(100vw - 48px)" className={cn("mx-auto max-w-144", "transition-opacity duration-1000 ease-in-out", {
            "-translate-x-7 scale-90": !initialIntroAnimationComplete,
            "translate-x-0 scale-100 transition-[opacity,translate,scale] duration-1000 ease-[cubic-bezier(0.33,1,0.68,1)]": !initialIntroAnimationComplete && cubeInview,
            "opacity-0": isSlideHidden
          })} /><div className="mt-23 lg:grid lg:grid-cols-12 lg:gap-6"><div className="lg:col-span-12 lg:col-start-1 xl:col-span-10 xl:col-start-2"><h3 {...createDataAttributes({
                test: "title"
              })} className={cn("text-h2 font-display mb-4", "delay-300 duration-600", {
                "delay-1000": !initialIntroAnimationComplete,
                "translate-y-6 opacity-0": isSlideHidden
              })}>{model_0.label}</h3><div className="grid gap-x-8 gap-y-6 lg:gap-y-8"><div className={cn("grid grid-cols-2 gap-x-4 gap-y-4 sm:gap-x-8 sm:gap-y-8 lg:grid-cols-4 lg:gap-x-10")} {...createDataAttributes({
                  test: "items"
                })}>{model_0.features?.map((feature, i_0) => <FeatureItem key={i_0} index={i_0} detail={feature.detail} style={{
                    transitionDelay: `${(initialIntroAnimationComplete ? 600 : 1000) + i_0 * 100}ms`
                  }} className={cn("duration-600", {
                    "translate-y-6 opacity-0": isSlideHidden
                  })} />)}</div></div>{!!model_0.href && <ButtonGroup className={cn("mt-6 w-full justify-center", "delay-1100 duration-600", {
                "delay-1400": !initialIntroAnimationComplete,
                "translate-y-6 opacity-0": isSlideHidden
              })}><Button href={model_0.href} className="w-full md:w-auto" color="black" {...createDataAttributes({
                  track: "cv-home",
                  section: "lineup",
                  text: "\u0E14\u0E39\u0E23\u0E32\u0E22\u0E25\u0E30\u0E40\u0E2D\u0E35\u0E22\u0E14",
                  url: model_0.href,
                  test: "button-cta"
                })} {...createDataGA({
                  click: true,
                  eventName: "lineup-cta",
                  group: "lineup-cta",
                  url: model_0.href,
                  text: "\u0E14\u0E39\u0E23\u0E32\u0E22\u0E25\u0E30\u0E40\u0E2D\u0E35\u0E22\u0E14"
                })}>ดูรายละเอียด</Button></ButtonGroup>}</div></div></div>;
      };
      $[73] = cubeInview;
      $[74] = initialIntroAnimationComplete;
      $[75] = selectedIndex;
      $[76] = t40;
    } else {
      t40 = $[76];
    }
    t39 = allModel.map(t40);
    $[68] = allModel;
    $[69] = cubeInview;
    $[70] = initialIntroAnimationComplete;
    $[71] = selectedIndex;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[77] !== t39) {
    t40 = <div className="pinch-zoom flex touch-pan-y will-change-transform">{t39}</div>;
    $[77] = t39;
    $[78] = t40;
  } else {
    t40 = $[78];
  }
  let t41;
  if ($[79] !== emblaRef || $[80] !== t40) {
    t41 = <div ref={emblaRef} className="w-full overflow-hidden" {...t38}>{t40}</div>;
    $[79] = emblaRef;
    $[80] = t40;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  let t42;
  let t43;
  if ($[82] !== scrollToAdjacentGroup) {
    t42 = () => scrollToAdjacentGroup(-1);
    t43 = () => scrollToAdjacentGroup(1);
    $[82] = scrollToAdjacentGroup;
    $[83] = t42;
    $[84] = t43;
  } else {
    t42 = $[83];
    t43 = $[84];
  }
  let t44;
  if ($[85] !== allModel.length || $[86] !== canScrollNextGroup || $[87] !== canScrollPrevGroup || $[88] !== cubeInview || $[89] !== emblaApi || $[90] !== groupPagination || $[91] !== onGroupPageChange || $[92] !== selectedIndex || $[93] !== t42 || $[94] !== t43) {
    t44 = <LineupCarouselOverlay categoryRef={categoryRef} cubeInview={cubeInview} groupPagination={groupPagination} canScrollPrevGroup={canScrollPrevGroup} canScrollNextGroup={canScrollNextGroup} selectedIndex={selectedIndex} totalModels={allModel.length} emblaApi={emblaApi} onGroupPageChange={onGroupPageChange} onScrollPrevGroup={t42} onScrollNextGroup={t43} />;
    $[85] = allModel.length;
    $[86] = canScrollNextGroup;
    $[87] = canScrollPrevGroup;
    $[88] = cubeInview;
    $[89] = emblaApi;
    $[90] = groupPagination;
    $[91] = onGroupPageChange;
    $[92] = selectedIndex;
    $[93] = t42;
    $[94] = t43;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  let t45;
  if ($[96] !== t37 || $[97] !== t41 || $[98] !== t44) {
    t45 = <div className="relative mt-[calc(48/360*100%)] sm:mt-24 lg:mt-0">{t37}{t41}{t44}</div>;
    $[96] = t37;
    $[97] = t41;
    $[98] = t44;
    $[99] = t45;
  } else {
    t45 = $[99];
  }
  let t46;
  if ($[100] !== t26 || $[101] !== t35 || $[102] !== t45) {
    t46 = <Container size="desktop-only">{t26}{t35}{t45}</Container>;
    $[100] = t26;
    $[101] = t35;
    $[102] = t45;
    $[103] = t46;
  } else {
    t46 = $[103];
  }
  let t47;
  if ($[104] !== scope || $[105] !== t46) {
    t47 = <Section {...t18} ref={scope}>{t46}</Section>;
    $[104] = scope;
    $[105] = t46;
    $[106] = t47;
  } else {
    t47 = $[106];
  }
  return t47;
}